// src/pages/Admin/VotePage/hooks/useVoteQuestions.js

import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  addChoiceToQuestion,
  addQuestionToVote,
  deleteChoice as deleteVoteChoice,
  deleteQuestion as deleteVoteQuestion,
  getVoteById,
  updateQuestion,
} from "../../../../services/voteServices";

const EMPTY_QUESTION = {
  question_text: "",
  type: "single",
};

export default function useVoteQuestions({
  isOpen,
  voteId,
}) {
  const [questions, setQuestions] = useState([]);
  const [questionForm, setQuestionForm] =
    useState(EMPTY_QUESTION);

  const [editingId, setEditingId] = useState(null);
  const [choiceDrafts, setChoiceDrafts] = useState({});

  const [loading, setLoading] = useState(false);
  const [savingQuestion, setSavingQuestion] =
    useState(false);

  const [processingChoiceId, setProcessingChoiceId] =
    useState(null);

  const loadQuestions = useCallback(async () => {
    if (!voteId) {
      return;
    }

    setLoading(true);

    try {
      const vote = await getVoteById(voteId);

      setQuestions(
        Array.isArray(vote?.questions) ? vote.questions : [],
      );
    } catch (error) {
      console.error(
        "Erreur lors du chargement des questions :",
        error,
      );

      toast.error("Impossible de charger les questions.");
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [voteId]);

  useEffect(() => {
    if (isOpen && voteId) {
      loadQuestions();
      return;
    }

    setQuestions([]);
    setQuestionForm(EMPTY_QUESTION);
    setEditingId(null);
    setChoiceDrafts({});
  }, [isOpen, voteId, loadQuestions]);

  const handleQuestionFieldChange = (event) => {
    const { name, value } = event.target;

    setQuestionForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
  };

  const startEditing = (question) => {
    setQuestionForm({
      question_text: question?.question_text || "",
      type: question?.type || "single",
    });

    setEditingId(question.id);
  };

  const cancelEditing = () => {
    setQuestionForm(EMPTY_QUESTION);
    setEditingId(null);
  };

  const saveQuestion = async (event) => {
    event?.preventDefault?.();

    if (!questionForm.question_text.trim()) {
      toast.warn("Le texte de la question est obligatoire.");
      return;
    }

    setSavingQuestion(true);

    try {
      if (editingId) {
        await updateQuestion(editingId, questionForm);
        toast.success("Question modifiée.");
      } else {
        await addQuestionToVote(voteId, questionForm);
        toast.success("Question ajoutée.");
      }

      cancelEditing();
      await loadQuestions();
    } catch (error) {
      console.error(
        "Erreur lors de l’enregistrement de la question :",
        error,
      );

      toast.error("La question n’a pas pu être enregistrée.");
    } finally {
      setSavingQuestion(false);
    }
  };

  const removeQuestion = async (question) => {
    const confirmed = window.confirm(
      `Supprimer la question « ${
        question?.question_text?.replace(/<[^>]*>/g, "") ||
        "sans titre"
      } » ?`,
    );

    if (!confirmed) {
      return;
    }

    try {
      await deleteVoteQuestion(question.id);

      if (editingId === question.id) {
        cancelEditing();
      }

      toast.success("Question supprimée.");
      await loadQuestions();
    } catch (error) {
      console.error(
        "Erreur lors de la suppression de la question :",
        error,
      );

      toast.error("La question n’a pas pu être supprimée.");
    }
  };

  const setChoiceDraft = (questionId, value) => {
    setChoiceDrafts((currentDrafts) => ({
      ...currentDrafts,
      [questionId]: value,
    }));
  };

  const addChoice = async (questionId) => {
    const choiceText =
      choiceDrafts[questionId]?.trim() || "";

    if (!choiceText) {
      toast.warn("Saisissez le texte de la réponse.");
      return;
    }

    setProcessingChoiceId(questionId);

    try {
      await addChoiceToQuestion(questionId, {
        choice_text: choiceText,
      });

      setChoiceDrafts((currentDrafts) => ({
        ...currentDrafts,
        [questionId]: "",
      }));

      toast.success("Proposition ajoutée.");
      await loadQuestions();
    } catch (error) {
      console.error(
        "Erreur lors de l’ajout de la proposition :",
        error,
      );

      toast.error("La proposition n’a pas pu être ajoutée.");
    } finally {
      setProcessingChoiceId(null);
    }
  };

  const removeChoice = async (choice) => {
    const confirmed = window.confirm(
      `Supprimer la proposition « ${
        choice?.choice_text || "sans titre"
      } » ?`,
    );

    if (!confirmed) {
      return;
    }

    setProcessingChoiceId(choice.id);

    try {
      await deleteVoteChoice(choice.id);

      toast.success("Proposition supprimée.");
      await loadQuestions();
    } catch (error) {
      console.error(
        "Erreur lors de la suppression de la proposition :",
        error,
      );

      toast.error(
        "La proposition n’a pas pu être supprimée.",
      );
    } finally {
      setProcessingChoiceId(null);
    }
  };

  return {
    questions,
    questionForm,
    editingId,
    choiceDrafts,
    loading,
    savingQuestion,
    processingChoiceId,
    handleQuestionFieldChange,
    startEditing,
    cancelEditing,
    saveQuestion,
    removeQuestion,
    setChoiceDraft,
    addChoice,
    removeChoice,
  };
}